import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { Transaction, Withdrawal, Rebate } from '../types';
import { mockApi } from '../services/mockData';
import { useTranslation } from '../hooks/useTranslation';
import { withdrawalApi, allHistoryApi } from '../services/api';
import { claimFromContract, hasWeb3Wallet } from '../services/claimContract';
import { showTelegramAlert } from '../services/telegramService';
import PageSkeleton from '../components/skeletons/PageSkeleton';

const History = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [rebates, setRebates] = useState<Rebate[]>([]);
  const [allHistory, setAllHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'transactions' | 'withdrawals' | 'rebates'>('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      const [txs, wds, rbs, allData] = await Promise.all([
        mockApi.getTransactions(user.id),
        withdrawalApi.getWithdrawals(),
        mockApi.getRebates(user.id),
        allHistoryApi.getHistory(1, 5),
      ]);
      
      setTransactions(txs);
      setWithdrawals(wds);
      setRebates(rbs);
      setAllHistory(allData.items || []);
      setHasMore(allData.pagination?.hasMore || false);
      setPage(1);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreHistory = useCallback(async () => {
    if (!user || loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const result = await allHistoryApi.getHistory(nextPage, 5);
      
      setAllHistory(prev => [...prev, ...(result.items || [])]);
      setHasMore(result.pagination?.hasMore || false);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [user, page, hasMore, loadingMore]);

  // Infinite scroll observer
  useEffect(() => {
    if (activeTab !== 'all') return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreHistory();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [activeTab, hasMore, loadingMore, loadMoreHistory]);

  const handleClaim = async (withdrawal: Withdrawal) => {
    if (!withdrawal.contractAddress) {
      showTelegramAlert(t('history.noHistoryFound'));
      return;
    }

    if (!hasWeb3Wallet()) {
      showTelegramAlert(t('common.error'));
      return;
    }

    const chainId = withdrawal.network.toLowerCase().includes('sepolia') ? 11155111 : 1;

    setClaiming(withdrawal.id);
    try {
      const result = await claimFromContract(withdrawal.contractAddress, chainId);
      
      if (result.success && result.txHash) {
        // Mark as claimed in backend
        await withdrawalApi.markClaimed(withdrawal.id, result.txHash);
        
        showTelegramAlert(t('history.claimedSuccessfully'));
        
        // Reload data
        loadData();
      } else {
        showTelegramAlert(result.error || t('history.failedToClaim'));
      }
    } catch (error: any) {
      showTelegramAlert(error.message || t('history.failedToClaim'));
    } finally {
      setClaiming(null);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatWalletAddress = (address: string, startLength: number = 12, endLength: number = 8) => {
    if (!address || address.length <= startLength + endLength) {
      return address;
    }
    return `${address.slice(0, startLength)}....${address.slice(-endLength)}`;
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return t('history.statusCompleted');
      case 'pending':
        return t('history.statusPending');
      case 'failed':
        return t('history.statusFailed');
      case 'processing':
        return t('history.statusProcessing');
      case 'rejected':
        return t('history.statusRejected');
      default:
        return status;
    }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  return (
    <div className="p-4 pb-24">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{t('history.title')}</h1>
            <p className="text-xs text-gray-400">{t('history.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        <button
          className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-300 ${
            activeTab === 'all' 
              ? 'bg-gradient-to-r from-primary to-yellow-500 text-black shadow-lg shadow-primary/40' 
              : 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-primary/30 hover:bg-gray-700/50'
          }`}
          onClick={() => setActiveTab('all')}
        >
          {t('common.all')}
        </button>
        <button
          className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-300 ${
            activeTab === 'transactions' 
              ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/40' 
              : 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-primary/30 hover:bg-gray-700/50'
          }`}
          onClick={() => setActiveTab('transactions')}
        >
          {t('history.transactions')}
        </button>
        <button
          className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-300 ${
            activeTab === 'withdrawals' 
              ? 'bg-gradient-to-r from-green-600 to-emerald-500 text-white shadow-lg shadow-green-500/40' 
              : 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-primary/30 hover:bg-gray-700/50'
          }`}
          onClick={() => setActiveTab('withdrawals')}
        >
          {t('history.withdrawals')}
        </button>
        <button
          className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition-all duration-300 ${
            activeTab === 'rebates' 
              ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg shadow-purple-500/40' 
              : 'bg-gray-800/50 text-gray-400 border border-gray-700 hover:border-primary/30 hover:bg-gray-700/50'
          }`}
          onClick={() => setActiveTab('rebates')}
        >
          {t('history.rebates')}
        </button>
      </div>

      <div className="flex flex-col gap-3">
        {activeTab === 'all' && (
          <>
            {allHistory.length > 0 ? (
              <>
                {allHistory.map((item) => {
                  if (item.type === 'transaction') {
                    return (
                      <div key={`tx-${item.id}`} className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-4 border border-gray-700/50 hover:border-primary/30 transition-all">
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-base font-semibold text-white capitalize">{item.transactionType}</div>
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            item.status === 'completed' 
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                              : item.status === 'pending'
                              ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {getStatusText(item.status)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-400 mb-2">{item.description}</div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-700/30">
                          <div className="text-xs text-gray-500">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </div>
                          <div className={`text-lg font-bold ${
                            item.status === 'completed' 
                              ? ((item.transactionType === 'bonus' || item.transactionType === 'rebate') ? 'text-primary' : 'text-red-400')
                              : 'text-orange-500'
                          }`}>
                            {item.status === 'completed' 
                              ? ((item.transactionType === 'bonus' || item.transactionType === 'rebate') ? '+' : '-')
                              : ''}{formatCurrency(Math.abs(item.amount))}
                          </div>
                        </div>
                      </div>
                    );
                  } else if (item.type === 'withdrawal') {
                    return (
                      <div key={`wd-${item.id}`} className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-4 border border-gray-700/50 hover:border-green-500/30 transition-all">
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-base font-semibold text-white">{item.network}</div>
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            item.status === 'completed' 
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                              : item.status === 'processing'
                              ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                              : item.status === 'pending'
                              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {getStatusText(item.status)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-400 mb-2 font-mono">{formatWalletAddress(item.walletAddress)}</div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-700/30">
                          <div className="text-xs text-gray-500">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </div>
                          <div className={`text-lg font-bold ${
                            item.status === 'completed' ? 'text-white' : 'text-orange-500'
                          }`}>
                            {item.status === 'completed' ? '-' : ''}{formatCurrency(item.amount)}
                          </div>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div key={`rb-${item.id}`} className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-4 border border-gray-700/50 hover:border-purple-500/30 transition-all">
                        <div className="flex justify-between items-center mb-2">
                          <div className="text-base font-semibold text-white uppercase">{item.exchange}</div>
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            item.status === 'completed' 
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                              : item.status === 'pending'
                              ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                              : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }`}>
                            {getStatusText(item.status)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-400 mb-2">{item.description}</div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-700/30">
                          <div className="text-xs text-gray-500">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </div>
                          <div className={`text-lg font-bold ${
                            item.status === 'completed' ? 'text-primary' : 'text-orange-500'
                          }`}>
                            {item.status === 'completed' ? '+' : ''}{formatCurrency(item.amount)}
                          </div>
                        </div>
                      </div>
                    );
                  }
                })}
                
                {/* Infinite Scroll Observer Target */}
                <div ref={observerTarget} className="h-4" />
                
                {/* Loading More Indicator */}
                {loadingMore && (
                  <div className="flex justify-center py-4">
                    <svg className="animate-spin h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
                
                {/* End of List */}
                {!hasMore && allHistory.length > 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    {t('history.noMoreItems')}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-400">
                {t('history.noHistoryFound')}
              </div>
            )}
          </>
        )}

        {activeTab === 'transactions' && (
          <>
            {transactions.length > 0 ? (
              transactions.map((tx) => (
                <div key={tx.id} className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-4 border border-gray-700/50 hover:border-primary/30 transition-all">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-base font-semibold text-white capitalize">{tx.type}</div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      tx.status === 'completed' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : tx.status === 'pending'
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                        : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                      {getStatusText(tx.status)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400 mb-2">{tx.description}</div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-700/30">
                    <div className="text-xs text-gray-500">
                      {new Date(tx.createdAt).toLocaleDateString()}
                    </div>
                    <div className={`text-lg font-bold ${
                      tx.status === 'completed' 
                        ? ((tx.type === 'bonus' || tx.type === 'rebate') ? 'text-primary' : 'text-red-400')
                        : 'text-orange-500'
                    }`}>
                      {tx.status === 'completed' 
                        ? ((tx.type === 'bonus' || tx.type === 'rebate') ? '+' : '-')
                        : ''}{formatCurrency(Math.abs(tx.amount))}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-400">{t('history.noTransactions')}</div>
            )}
          </>
        )}

        {activeTab === 'withdrawals' && (
          <>
            {withdrawals.length > 0 ? (
              withdrawals.map((withdrawal) => (
                <div key={withdrawal.id} className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-4 border border-gray-700/50 hover:border-green-500/30 transition-all">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-base font-semibold text-white">{withdrawal.network}</div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      withdrawal.status === 'completed' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : withdrawal.status === 'processing'
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                        : withdrawal.status === 'pending'
                        ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                        : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                      {getStatusText(withdrawal.status)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400 mb-2 font-mono">{formatWalletAddress(withdrawal.walletAddress)}</div>
                  
                  {/* Claim button for claimable withdrawals */}
                  {withdrawal.claimStatus === 'claimable' && withdrawal.status === 'processing' && (
                    <div className="mt-3 pt-3 border-t border-gray-700">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-xs text-primary font-medium">
                          {t('history.readyToClaim')}
                        </span>
                      </div>
                      <button
                        onClick={() => handleClaim(withdrawal)}
                        disabled={claiming === withdrawal.id}
                        className="w-full py-2.5 bg-primary text-black rounded-lg font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {claiming === withdrawal.id ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('history.claiming')}
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {t('history.claimNow')} ({t('history.payGas')})
                          </>
                        )}
                      </button>
                      <p className="text-xs text-tg-hint mt-2 text-center">
                        ⚠️ {t('common.error')}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center pt-2 border-t border-gray-700/30 mt-2">
                    <div className="text-xs text-gray-500">
                      {new Date(withdrawal.createdAt).toLocaleDateString()}
                    </div>
                    <div className={`text-lg font-bold ${
                      withdrawal.status === 'completed' ? 'text-white' : 'text-orange-500'
                    }`}>
                      {withdrawal.status === 'completed' ? '-' : ''}{formatCurrency(withdrawal.amount)}
                    </div>
                  </div>
                  
                  {/* Show transaction hash if available */}
                  {withdrawal.txHash && withdrawal.claimStatus !== 'claimable' && (
                    <div className="mt-2 pt-2 border-t border-gray-700/30">
                      <a 
                        href={`https://sepolia.etherscan.io/tx/${withdrawal.txHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:text-blue-300 font-mono truncate block"
                      >
                        Tx: {formatWalletAddress(withdrawal.txHash, 16, 8)}
                      </a>
                    </div>
                  )}
                  
                  {/* Show claimed info */}
                  {withdrawal.claimStatus === 'claimed' && withdrawal.claimTxHash && (
                    <div className="mt-2 pt-2 border-t border-gray-700/30">
                      <div className="text-xs text-green-400 mb-1 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {t('history.claimed')}
                      </div>
                      <a 
                        href={`https://sepolia.etherscan.io/tx/${withdrawal.claimTxHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:text-blue-300 font-mono truncate block"
                      >
                        Claim Tx: {formatWalletAddress(withdrawal.claimTxHash, 16, 8)}
                      </a>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-400">{t('history.noWithdrawals')}</div>
            )}
          </>
        )}

        {activeTab === 'rebates' && (
          <>
            {rebates.length > 0 ? (
              rebates.map((rebate) => (
                <div key={rebate.id} className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-4 border border-gray-700/50 hover:border-purple-500/30 transition-all">
                  <div className="flex justify-between items-center mb-2">
                    <div className="text-base font-semibold text-white uppercase">{rebate.exchange}</div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      rebate.status === 'completed' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : rebate.status === 'pending'
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                        : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                      {getStatusText(rebate.status)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-400 mb-2">{rebate.description}</div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-700/30">
                    <div className="text-xs text-gray-500">
                      {new Date(rebate.createdAt).toLocaleDateString()}
                    </div>
                    <div className={`text-lg font-bold ${
                      rebate.status === 'completed' ? 'text-primary' : 'text-orange-500'
                    }`}>
                      {rebate.status === 'completed' ? '+' : ''}{formatCurrency(rebate.amount)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-400">{t('history.noRebates')}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default History;

